import { Controller } from '@hotwired/stimulus';
import graphql from 'stimulus/graphql';

// The proposal is created as soon as it is made, not when the step is submitted: it is a
// ProposedTag belonging to the job ad, which the reviewers pick up whether or not the
// employer ever finishes the wizard. The page decides what to show for it — this only
// reports what was created.
const CREATE_PROPOSED_TAG = `
  mutation ($name: String!, $description: String!, $jobAdId: ID, $category: ID) {
    createProposedTag(name: $name, description: $description, jobAdId: $jobAdId, category: $category) {
      resource { id name }
      errors { field message }
    }
  }
`;

const NAME_MAX_LENGTH = 50;

export default class extends Controller {
  static targets = ['name', 'description', 'submit'];

  static outlets = ['modal-component', 'input-field-component', 'plain-text-editor-component'];

  static values = {
    jobAdId: String,
    category: String
  };

  open(query = '') {
    this.nameTarget.value = query;
    this.descriptionTarget.value = '';
    this.clearErrors();
    this.modalComponentOutlet.open();
    requestAnimationFrame(() => this.descriptionTarget.focus());
  }

  cancel() {
    this.modalComponentOutlet.forceClose();
  }

  async submit(event) {
    event?.preventDefault();
    if (this.submitTarget.disabled || !this.valid()) return;

    this.submitTarget.disabled = true;

    try {
      const { resource } = await this.create();
      this.modalComponentOutlet.forceClose();
      this.dispatch('created', { detail: { value: resource.id, label: resource.name } });
    } catch (error) {
      this.nameField().showError(error.message);
    } finally {
      this.submitTarget.disabled = false;
    }
  }

  create() {
    return graphql(CREATE_PROPOSED_TAG, {
      name: this.nameTarget.value.trim(),
      description: this.descriptionTarget.value.trim(),
      jobAdId: this.jobAdIdValue,
      category: this.categoryValue
    }, 'createProposedTag');
  }

  valid() {
    this.clearErrors();

    const name = this.nameTarget.value.trim();
    const description = this.descriptionTarget.value.trim();

    if (!name) return this.reject(this.nameField(), "This field can't be empty.");
    if (name.length > NAME_MAX_LENGTH) {
      return this.reject(this.nameField(),
        `Skill name cannot be longer than ${NAME_MAX_LENGTH} characters.`);
    }
    if (/[,;]/.test(name)) return this.reject(this.nameField(), "Skill name cannot contain ',' or ';'.");
    if (!description) return this.reject(this.descriptionField(), "This field can't be empty.");

    return true;
  }

  reject(field, message) {
    field.showError(message);

    return false;
  }

  clearErrors() {
    this.nameField().clearError();
    this.descriptionField().clearError();
  }

  // The kit fields own how an error is rendered; reached through outlets so this only
  // decides what it says.
  nameField() {
    return this.inputFieldComponentOutlet;
  }

  descriptionField() {
    return this.plainTextEditorComponentOutlet;
  }
}
